import { GetServerSidePropsContext } from 'next';
import cookie from 'cookie';
import { useTranslation } from 'react-i18next';

import { Box, Flex, useToast } from '@chakra-ui/react';
import {
  CustomCertificate,
  DownloadCustomCertificateButton,
  ScaleContainer,
  ShareButton,
} from '@udacity/udacity-certificate';

import { ContentContainer } from '~/components/container';
import { PageCategory } from '~/features/analytics/constants';
import { trackEvent } from '~/features/analytics/track-event';
import { MarketingMessage } from '~/features/certificates/components/marketing-message';
import { getCertV2ByLearningPlanSubscriptionId } from '~/features/certificates/controllers/get-certificate';
import { useLocalizedCertificateV2 } from '~/features/certificates/hooks/use-localized-certificate-v2';
import { CertificateV2 } from '~/features/certificates/model';
import { setCertificatePublicCacheIfUnauthenticated } from '~/features/certificates/set-certificate-cache';
import { getUserLearningPlans } from '~/features/learning-plans/controllers/get-user-learning-plans';
import { UserLearningPlans } from '~/features/learning-plans/models/user-learning-plans';
import { PageHead } from '~/features/page/components/page-head';
import { PageLayout } from '~/features/page/components/page-layout';
import { Page } from '~/features/page/models/page';
import { getInternalPage } from '~/features/page/utils/get-internal-page';
import { getUserPreferences } from '~/features/user/controllers/get-user-preferences';
import { getUserIdFromJwt } from '~/features/user/utils/get-user-id-from-jwt';

export default function PlanCertificatePage({
  page,
  initialCertificate,
  initialLanguage,
  subscriptionId,
  userLearningPlan,
}: {
  page: Page;
  initialCertificate: NonNullable<CertificateV2>;
  initialLanguage: string;
  userLearningPlan: UserLearningPlans[number] | null;
  subscriptionId: string;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const { certificate: localizedCertificate } = useLocalizedCertificateV2({
    subscriptionId,
    initialLanguage,
  });
  const certificate = localizedCertificate || initialCertificate;
  const trackingAttributes = getTrackingAttributes(userLearningPlan);

  return (
    <Box>
      <style>
        {`
          @media print{
            html, body, #__next {
              overflow: hidden; !important
            }
          }
        `}
      </style>
      <PageHead page={page} pageCategory={PageCategory.Uncategorized} />
      <PageLayout isSimpleMode>
        <ContentContainer>
          <Flex
            gap='24px'
            id='certificate-container'
            maxW='100vw'
            flexDir={{
              base: 'column',
              md: 'row',
            }}
          >
            <Box
              w={{
                base: '100%',
                md: '67%',
              }}
            >
              <ScaleContainer>
                <CustomCertificate certificate={certificate} />
              </ScaleContainer>
            </Box>
            <Box flex={1} minW='300px'>
              <MarketingMessage />
              <Flex gap='16px'>
                <DownloadCustomCertificateButton />
                {userLearningPlan && (
                  <ShareButton
                    confirmUrl={getConfirmUrl(subscriptionId)}
                    graduationDate={certificate.completionDate.content}
                    programTitle={userLearningPlan.title}
                    onCopyToClipboardClick={() => {
                      toast({
                        variant: 'success',
                        title: t('certificate.linkCopied'),
                      });
                    }}
                    onLinkedInClick={() => {
                      trackEvent({
                        type: 'click',
                        name: 'Share Learning Plan Certificate on LinkedIn Clicked',
                        label: 'Share on LinkedIn',
                        action: 'share_lp_certificate_on_linkedin_clicked',
                        additionalAttributes: trackingAttributes,
                      });
                    }}
                    onShareClick={() => {
                      trackEvent({
                        type: 'click',
                        name: 'Share Learning Plan Certificate Clicked',
                        label: 'Share',
                        action: 'share_lp_certificate_clicked',
                        additionalAttributes: trackingAttributes,
                      });
                    }}
                  />
                )}
              </Flex>
            </Box>
          </Flex>
        </ContentContainer>
      </PageLayout>
    </Box>
  );
}

export const getServerSideProps = async ({ req, res, params }: GetServerSidePropsContext) => {
  const cookies = cookie.parse(req.headers.cookie ?? '');
  const jwt = cookies._jwt;

  const subscriptionId = params?.subscriptionId;
  if (!subscriptionId) {
    setCertificatePublicCacheIfUnauthenticated(res, jwt);
    return {
      notFound: true,
    };
  }

  const userPreferences = jwt ? await getUserPreferences({ jwt }) : undefined;
  const initialLanguage = userPreferences?.preferredLanguage ?? 'en-US';

  let certificate: CertificateV2 | null = null;

  try {
    certificate = await getCertV2ByLearningPlanSubscriptionId({
      subscriptionId: subscriptionId as string,
      preferredLanguage: initialLanguage,
    });
  } catch {
    setCertificatePublicCacheIfUnauthenticated(res, jwt);
    return {
      notFound: true,
    };
  }

  if (!certificate) {
    setCertificatePublicCacheIfUnauthenticated(res, jwt);
    return {
      notFound: true,
    };
  }

  const page = getInternalPage({ eventName: 'Certificate Page Viewed', nofollow: false });
  const userId = jwt ? getUserIdFromJwt(jwt) : undefined;

  const userLearningPlans = userId ? await getUserLearningPlans({ userId, jwt, forcedLanguage: initialLanguage }) : [];
  const userLearningPlan = userLearningPlans.find((plan) => plan.subscriptionId === subscriptionId);

  setCertificatePublicCacheIfUnauthenticated(res, jwt);

  return {
    props: {
      page,
      initialCertificate: certificate,
      initialLanguage,
      subscriptionId,
      userLearningPlan: userLearningPlan ?? null,
    },
  };
};

function getConfirmUrl(subscriptionId: string) {
  if (typeof window === 'undefined') {
    return `https://www.udacity.com/certificate/lp/${subscriptionId}`;
  }
  return window.location.href;
}

function getTrackingAttributes(userLearningPlan: UserLearningPlans[number] | null) {
  if (!userLearningPlan) return {};

  const purchaseId = userLearningPlan.tags?.find((tag) => tag.tagType === 'PURCHASE_ID')?.tagKey;
  const contractId = userLearningPlan.tags?.find((tag) => tag.tagType === 'ENTERPRISE_CONTRACT')?.tagKey;

  return {
    plan_id: userLearningPlan.id,
    company_id: userLearningPlan.companyId,
    purchase_id: purchaseId,
    contract_id: contractId,
  };
}
